import {tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import NavatticDemo from '@/components/navattic-demo'
import type {InteractiveDemoSliceFragment} from '@/graphql-types'
import Button from '@helios/shared/components/button'
import Image from '@helios/shared/components/image'
import {
    ModalContent,
    dialogContentStyles,
    ModalImageAndTitles,
} from '@helios/shared/components/modal'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {Layout, Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import getEnvironmentField from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

export const InteractiveDemoSlice = ({primary, items}: InteractiveDemoSliceFragment) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(true)
    const interactiveDemoEnabled = useMediaQuery.min('md')

    const iframeSource = getEnvironmentField(
        primary.interactive_demo_link_production.url,
        primary.interactive_demo_link_staging.url
    )

    return (
        <>
            <Wrapper>
                {primary.title && (
                    <div>
                        <Text variant="heading-large" as="h2" center>
                            {primary.title}
                        </Text>

                        {primary.subtitle && (
                            <Subtitle variant="paragraph-large" center>
                                {primary.subtitle}
                            </Subtitle>
                        )}
                    </div>
                )}

                <Layout variant="three-equal">
                    {items.map((item, i) => (
                        <ItemWrapper key={`${item.title}-${i}`}>
                            <ImageTitleWrapper>
                                {item.image && (
                                    <Image image={item.image} objectFit="contain" width={380} />
                                )}

                                <Text variant="title-base" center>
                                    {item.title}
                                </Text>
                            </ImageTitleWrapper>

                            {hasRichText(item.body1.richText) && (
                                <RichText bgColor="black" center content={item.body1.richText} />
                            )}
                        </ItemWrapper>
                    ))}
                </Layout>
            </Wrapper>

            {interactiveDemoEnabled && (
                <Section>
                    <IframeWrapper>
                        {modalIsOpen && (
                            <StyledModal>
                                <ModalWrapper>
                                    <ModalContent>
                                        <ModalImageAndTitles
                                            title={primary.modal_title}
                                            subtitle={primary.modal_subtitle}
                                            image={primary.modal_image}
                                        />
                                    </ModalContent>
                                    <ButtonWrapper>
                                        <Button type="button" onClick={() => setModalIsOpen(false)}>
                                            {primary.modal_button_text}
                                        </Button>
                                    </ButtonWrapper>
                                </ModalWrapper>
                            </StyledModal>
                        )}
                        <NavatticDemo
                            title={primary.modal_title}
                            src={iframeSource}
                            tabIndex={modalIsOpen ? -1 : 0}
                        />
                    </IframeWrapper>
                </Section>
            )}
        </>
    )
}

const Wrapper = styled(Section)`
    display: grid;
    gap: ${tokens.spacing40};

    ${mq.min('sm')} {
        gap: ${tokens.spacing60};
    }
`

const Subtitle = styled(Text)`
    margin-top: ${tokens.spacing10};
`

const ItemWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing16};
`

const ImageTitleWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        gap: ${tokens.spacing40};
    }

    img {
        width: 100%;
        max-height: 250px;
        object-fit: contain;
    }
`

const IframeWrapper = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 2;
`

const StyledModal = styled.div`
    position: absolute;
    inset: 0;
    background: ${tokens.shade900lighten50};
    display: grid;
    align-items: center;
    justify-content: center;
`

const ModalWrapper = styled.div`
    ${dialogContentStyles}
`

const ButtonWrapper = styled.div`
    padding: ${tokens.spacing40};

    button {
        width: 100%;
    }
`

export const interactiveDemoSliceFragment = graphql`
    fragment InteractiveDemoSlice on PrismicSlicePageDataBodyInteractiveDemo {
        __typename
        primary {
            title
            subtitle
            interactive_demo_link_production {
                url
            }
            interactive_demo_link_staging {
                url
            }
            modal_title
            modal_subtitle
            modal_button_text
            modal_image {
                alt
                url
                copyright
                dimensions {
                    height
                    width
                }
            }
        }
        items {
            title
            body1 {
                richText
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
            }
        }
    }
`
